import React from "react";
import {
  Paginator,
  Box,
  EmptyState,
  Button,
  Typography,
} from "@tiny/components";
import SuppressionListFilter from "./SuppressionListFilter";
import { SuppressionListProvider } from "./SuppressionList.context";
import { useSuppressionList } from "./SuppressionList.hooks";
import { SuppressionListProps } from "./SuppressionList.types";
import { useStyles } from "./SuppressionList.styles";
import SuppressionListRow from "./SuppressionListRow";
import UserMinus from "@tiny/icons/generated/UserMinus";
import ComponentLoader from "@tiny/components/src/ComponentLoader/ComponentLoader";
import useSuppressionImporter from "../AddSuppressionDialog/useSupperessionImporter";
import AddSuppressionDialog from "../AddSuppressionDialog";
import TinySuppressionImporter from "../AddSuppressionDialog/TinySuppressionImporter";
import { SuppressionData } from "../AddAudienceButton/AddAudienceButton";

function SuppressionList(props: SuppressionListProps) {
  const suppressionListView = useSuppressionList(props);

  const classes = useStyles();

  const {
    audiences,
    boxProps,
    page,
    itemsPerPage,
    itemsPerPageOptions,
    onPageChange,
    onItemsPerPageChange,
    isEmpty,
    visible,
    isSegmentLoading,
    reloadSuppressionList,
    canToggleScope,
    isCampaignMode,
  } = suppressionListView;

  const [suppressionDetails, setSuppressionDetails] = React.useState<
    SuppressionData | undefined
  >(undefined);

  const {
    suppressionImporterOpen,
    openSuppressionImporter,
    closeSuppressionImporter,
    addSuppressionDialogOpen,
    openAddSuppressionDialog,
    closeAddSuppressionDialog,
  } = useSuppressionImporter();

  if (!visible) {
    return null;
  }

  return (
    <>
      <SuppressionListProvider value={suppressionListView}>
        <Box {...boxProps}>
          <SuppressionListFilter />
          {isSegmentLoading ? (
            <ComponentLoader height="400px" />
          ) : isEmpty ? (
            <Box bgcolor="grey.50" pt={10} pb={10}>
              <EmptyState
                title="No suppression list"
                message={
                  <>
                    <Typography
                      variant="body2"
                      component="p"
                      color="grey"
                      tone={700}
                      m={0}
                    >
                      You have not created any suppression list.
                    </Typography>
                    <Typography
                      variant="body2"
                      component="p"
                      color="grey"
                      tone={700}
                      m={0}
                    >
                      Get started by uploading a new list and get your word out
                      into the world.
                    </Typography>
                  </>
                }
                icon={<UserMinus />}
                iconColor="primary"
                circleColor="white"
                align="center"
              >
                <Button variant="outlined" onClick={openAddSuppressionDialog}>
                  Upload Now
                </Button>
              </EmptyState>
            </Box>
          ) : (
            <ul className={classes.list}>
              {audiences?.content?.map((audience) => (
                <SuppressionListRow
                  key={audience.id}
                  suppression={audience}
                  canToggleScope={canToggleScope}
                  isCampaignMode={isCampaignMode}
                />
              ))}
            </ul>
          )}
          <Paginator
            mt={4}
            itemsPerPageOptions={itemsPerPageOptions}
            page={page}
            itemsPerPage={itemsPerPage}
            totalCount={audiences?.totalElements}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPageLabel="Results"
          />
        </Box>
        <AddSuppressionDialog
          open={addSuppressionDialogOpen}
          onClose={closeAddSuppressionDialog}
          onSuccess={(data) => {
            setSuppressionDetails(data);
            openSuppressionImporter();
          }}
        />
        <TinySuppressionImporter
          open={suppressionImporterOpen}
          onClose={closeSuppressionImporter}
          suppressionDetails={suppressionDetails}
          onSuccess={() => {
            reloadSuppressionList();
          }}
          suppressionType={suppressionDetails?.type}
        />
      </SuppressionListProvider>
    </>
  );
}

export default SuppressionList;
